import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SheetPreviewModal from './SheetPreviewModal';
import './FileInfoPanel.css';

interface FileInfoProps {
  fileInfo: {
    name?: string;
    total_sheets?: number;
    sheet_names?: string[];
    total_rows_all_sheets?: number;
    max_columns?: number;
    file_format?: string;
  };
  taskId?: string;
}

const FileInfoPanel: React.FC<FileInfoProps> = ({ fileInfo, taskId }) => {
  const { t } = useTranslation('create');
  const [previewSheet, setPreviewSheet] = useState<string | null>(null);

  if (!fileInfo || Object.keys(fileInfo).length === 0) {
    return null;
  }

  return (
    <div className="file-info-panel">
      <div className="file-info-columns">
        <div className="file-info-left-column">
          <div className="instruction-tips">
            <div className="instruction-tip">
              <div className="tip-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 11L12 14L15 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 3V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 21H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="tip-content">
                <h4>{t('file_info.tip1_title')}</h4>
                <p>{t('file_info.tip1_desc')}</p>
              </div>
            </div>

            <div className="instruction-tip">
              <div className="tip-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 7L18 8M19 7L20 8M19 7V4M19 4H16M19 4H22M12 21V16M12 16L9 19M12 16L15 19M5 7L4 8M5 7L6 8M5 7V4M5 4H2M5 4H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="tip-content">
                <h4>{t('file_info.tip2_title')}</h4>
                <p>{t('file_info.tip2_desc')}</p>
              </div>
            </div>

            <div className="instruction-tip">
              <div className="tip-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9V12M12 12V15M12 12H15M12 12H9M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="tip-content">
                <h4>{t('file_info.tip3_title')}</h4>
                <p>{t('file_info.tip3_desc')}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="file-info-right-column">
          <div className="file-info-header">
            <div className="file-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2V8H20" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 13H8" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 17H8" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 9H9H8" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="file-title">
              <h3>{fileInfo.name || t('file_info.excel_file')}</h3>
              <span className="file-format">{fileInfo.file_format?.toUpperCase() || 'XLSX'}</span>
            </div>
          </div>

          <div className="file-stats">
            <div className="stat-item">
              <div className="stat-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 9H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 21V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="stat-content">
                <span className="stat-value">{fileInfo.total_sheets || 0}</span>
                <span className="stat-label">{t('file_info.sheets')}</span>
              </div>
            </div>

            <div className="stat-item">
              <div className="stat-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 6H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 6H3.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 12H3.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 18H3.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="stat-content">
                <span className="stat-value">{fileInfo.total_rows_all_sheets || 0}</span>
                <span className="stat-label">{t('file_info.rows')}</span>
              </div>
            </div>

            <div className="stat-item">
              <div className="stat-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 3H3V21H21V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 3V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 9H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="stat-content">
                <span className="stat-value">{fileInfo.max_columns || 0}</span>
                <span className="stat-label">{t('file_info.columns')}</span>
              </div>
            </div>
          </div>

          {fileInfo.sheet_names && fileInfo.sheet_names.length > 0 && (
            <div className="sheet-list">
              <h4>{t('file_info.sheets_in_file')}</h4>
              <div className="sheet-tags">
                {fileInfo.sheet_names.map((sheet, index) => (
                  <span
                    key={index}
                    className={`sheet-tag${taskId ? ' sheet-tag-clickable' : ''}`}
                    onClick={taskId ? () => setPreviewSheet(sheet) : undefined}
                  >
                    {sheet}
                    {taskId && (
                      <svg className="sheet-tag-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="12" height="12">
                        <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {previewSheet && taskId && (
        <SheetPreviewModal
          taskId={taskId}
          sheetName={previewSheet}
          onClose={() => setPreviewSheet(null)}
        />
      )}
    </div>
  );
};

export default FileInfoPanel;
